import { forwardRef, useMemo } from 'react'
import { traceColor, type LapTrace } from '../analysis/laps'
import type { ReportContext } from '../lib/report'
import { formatDate } from '../lib/dates'
import ChannelChart from './ChannelChart'
import SectionSummary from './SectionSummary'
import TrackMap from './TrackMap'
import AccelLegend from './AccelLegend'
import { scaleForTraces } from '../analysis/accel'
import type { AccelMode } from '../lib/persist'
import TractionCircle from './TractionCircle'
import { tractionForTrace, type TraceTraction } from '../analysis/traction'
import { useBike } from '../lib/bike'

export interface ReportInclude {
  summary: boolean
  charts: boolean
  map: boolean
}

export interface ReportProps {
  ctx: ReportContext
  include: ReportInclude
  /** The diagrams as shown in the studio */
  showDelta: boolean
  accelMode: AccelMode
  showTraction: boolean
  channelNames: string[]
  units: Map<string, string>
  accessors: Map<string, (t: LapTrace) => Float64Array | undefined>
  deltaValues: (t: LapTrace) => Float64Array | undefined
  note?: string
}

/** Fixed width of the exported image, in CSS px (rendered at 2x). */
export const REPORT_WIDTH = 1100

/**
 * The comparison laid out for export: a fixed-width page with the laps, the diagrams, the
 * racing lines and the summary. Rendered off screen and rasterized to a PNG.
 */
const ReportView = forwardRef<HTMLDivElement, ReportProps>(function ReportView(
  { ctx, include, showDelta, accelMode, showTraction, channelNames, units, accessors, deltaValues, note },
  ref,
) {
  const { traces, refTrace, sections } = ctx
  const secs = sections.length ? sections : undefined
  const bike = useBike()
  const traction = useMemo(
    () => new Map<string, TraceTraction | null>(traces.map((t) => [t.key, tractionForTrace(t, bike)])),
    [traces, bike],
  )
  return (
    <div ref={ref} className="report" style={{ width: REPORT_WIDTH }}>
      <header className="report-head">
        <div>
          <strong>Mala Racing</strong> · {ctx.track ?? 'Unknown track'}
          {ctx.date ? ` · ${formatDate(ctx.date)}` : ''}
        </div>
        <ul className="legend">
          {traces.map((t, i) => (
            <li key={t.key}>
              <span className="swatch" style={{ background: traceColor(t, i), marginLeft: 0 }} />
              {t.label} <small className="muted">{fmtLap(t.lap.duration)}</small>
              {t === refTrace && <small className="muted"> · reference</small>}
            </li>
          ))}
        </ul>
      </header>
      {include.charts && (
        <div className="report-charts">
          {showDelta && (
            <ChannelChart
              title={`Δ time vs ${refTrace.label}`}
              unit="s"
              traces={traces}
              values={deltaValues}
              syncKey="report"
              height={240}
              zeroLine
              sections={secs}
            />
          )}
          {channelNames.map((name) => (
            <ChannelChart
              key={name}
              title={name}
              unit={units.get(name) ?? ''}
              traces={traces}
              values={accessors.get(name)!}
              syncKey="report"
              height={300}
              sections={secs}
            />
          ))}
          {showTraction && (
            <div className="report-traction">
              <h3>Traction circle</h3>
              <TractionCircle
                traces={traces}
                traction={traction}
                cursorDistance={null}
                height={380}
                syncKey="report"
                still
              />
            </div>
          )}
        </div>
      )}
      {include.map && (
        <div className="report-map">
          <h3>Racing lines</h3>
          <TrackMap
            traces={traces}
            cursorDistance={null}
            height={520}
            syncKey="report"
            sections={secs}
            refTrace={refTrace}
            accel={accelMode}
          />
          {accelMode !== 'off' && <AccelLegend scale={scaleForTraces(traces).scale} mode={accelMode} />}
        </div>
      )}
      {include.summary && (
        <SectionSummary
          traces={traces}
          refTrace={refTrace}
          sections={sections}
          channels={ctx.channels}
          active={-1}
          note={note}
        />
      )}
    </div>
  )
})

export default ReportView

function fmtLap(seconds: number): string {
  const m = Math.floor(seconds / 60)
  return `${m}:${(seconds - m * 60).toFixed(3).padStart(6, '0')}`
}
